import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Badge,
  HStack,
  IconButton,
  Table,
  VStack,
  Text,
  Box,
  Flex,
  Input, Portal, createListCollection, Select, Button, Field, Dialog
} from "@chakra-ui/react";
import { getUsersList } from "../Redux/Action/auth/getUsersListAction";
import { FaRegCopy } from "react-icons/fa6";
import { IoCheckmarkDoneOutline } from "react-icons/io5";
import { FaRegEye, FaRegEyeSlash, FaInfoCircle } from "react-icons/fa";
import { Tooltip } from "../Components/ui/tooltip";
import { CustomPagination } from "../Components/custom/CustomPagination";
import { UserListSkeleton } from "../Components/custom/Skeletons/UserListSkeleton";
import ToggleButton from "../Components/ui/ToggleButton";
import { deleteUser, updateUserStatus, adminSubmitUserKyc } from "../Redux/Action";
import { FiTrash2, FiEdit, FiDownload, FiX, FiUpload, FiSearch, FiTrendingUp, FiPlusCircle } from "react-icons/fi";
import { MultiImageDialog } from "../Components/custom/CustomModal";
import { KYC_KEYS, S3_BUCKET_BASE_URL, ADD_BALANCE_PARAMS } from "../Utils/Constants";
import { PasswordChangeModal } from "../Components/custom/PassWordChangeModal";
import { PromoCodeEditModal } from "../Components/custom/PromoCodeEditModal";
import { ViewPasswordModal } from "../Components/custom/ViewPasswordModal";
import { getLocalStorageItem, extractUsersFromData } from "../Utils/Helper";
import { IBDetailPage } from "./IBDetailPage";
import { setDynamicTitle } from "../Utils/dynamicTitleManager";
import { addUserBalance } from "../Redux/Action/dashboard/addUserBalanceAction";
import { KYCDocumentUploadModal } from "../Components/custom/KYCDocumentUploadModal";
import { NineXTradingAccountCard, isNineXTradingUser } from "../Components/custom/NineXTradingAccountCard";

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import UserTradesModal from "../Components/custom/UserTradesModal";

const KYC_DOCUMENT_LABELS = {
  PASSPORT: "Passport",
  NATIONAL_ID: "National ID card",
  DRIVING_LICENSE: "Driving licence",
  RESIDENCE_PERMIT: "Residence permit",
  PROOF_OF_ADDRESS: "Proof of address",
  OTHER: "Other document",
};

const KYC_STATUS_STYLES = {
  0: { label: "Rejected", bg: "red.50", color: "red.700", dot: "red.500" },
  1: { label: "Accepted", bg: "green.50", color: "green.700", dot: "green.500" },
  2: { label: "Pending", bg: "orange.50", color: "orange.700", dot: "orange.400" },
  3: { label: "Not Submitted", bg: "gray.100", color: "gray.700", dot: "gray.400" },
};

// Compact icon button with a tooltip, used for every row action
// Tooltip body: the action name plus, when given, what clicking it does
const ActionTooltipContent = ({ label, description }) =>
  description ? (
    <Box maxW="240px" py={0.5}>
      <Text fontWeight="600" fontSize="xs">{label}</Text>
      <Text fontSize="xs" opacity={0.85} mt={0.5}>{description}</Text>
    </Box>
  ) : (
    label
  );

// Fixed-width slots so the labels in the Actions header line up with the icons
const ACTION_SLOT_WIDTH = "64px";
const ACTION_COLUMNS = ["Status", "Trades", "Add Funds", "Delete"];

const ActionIcon = ({ label, description, palette = "gray", onClick, children }) => (
  <Tooltip content={<ActionTooltipContent label={label} description={description} />} showArrow>
    <IconButton
      aria-label={label}
      variant="ghost"
      size="xs"
      colorPalette={palette}
      color={palette === "gray" ? "fg.muted" : `${palette}.600`}
      _hover={{ bg: palette === "gray" ? "gray.100" : `${palette}.50`, color: palette === "gray" ? "fg.default" : `${palette}.700` }}
      onClick={onClick}
    >
      {children}
    </IconButton>
  </Tooltip>
);

export const UsersList = () => {
  // Use dynamic title management
  useEffect(() => {
    setDynamicTitle("Users List");
  }, []);

  const dispatch = useDispatch();
  const whiteLabelDetail = useSelector(
    (state) => state?.GetWhiteLabel?.whitelabelData
  );
  const [allUsersList, setAllUsersList] = useState([]);
  const [isDataLoading, setisDataLoading] = useState(true);
  const [totalPagesCount, setTotalPagesCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowCount, setRowCount] = useState(10);
  const [copiedId, setCopiedId] = useState(null);
  const [copiedType, setCopiedType] = useState(null);
  const [refetch, setRefetch] = useState(false);
  const [kycData, setKycData] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editPassword, setEditPassword] = useState(false);
  const [editPasswordData, setEditPasswordData] = useState(null);
  const [editPromoCode, setEditPromoCode] = useState(false);
  const [editPromoCodeData, setEditPromoCodeData] = useState(null);
  const [viewPasswordUser, setViewPasswordUser] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const whitelabelLocalStorage = getLocalStorageItem("whitelabelData");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const SPECIAL_CHARS = /^[!@#$%^&*()]/;
  const [selectedIB, setSelectedIB] = useState(null);
  const [type, setType] = useState(null);
  // New state for deposit modal
  const [isDepositModalOpen, setIsDepositModalOpen] = useState(false);
  const [selectedUserForDeposit, setSelectedUserForDeposit] = useState(null);
  const [depositAmount, setDepositAmount] = useState("");
  const [adminPassword, setAdminPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  
  // New state for trades modal
  const [isTradesModalOpen, setIsTradesModalOpen] = useState(false);
  const [selectedUserForTrades, setSelectedUserForTrades] = useState(null);

  // State for KYC document upload modal
  const [isKycUploadModalOpen, setIsKycUploadModalOpen] = useState(false);
  const [selectedUserForKycUpload, setSelectedUserForKycUpload] = useState(null);

  // State for animated placeholder
  const [placeholderText, setPlaceholderText] = useState("Username");
  const [isAnimating, setIsAnimating] = useState(false);

  // Handle animated placeholder effect
  useEffect(() => {
    const texts = ["Username", "First name", "Last name"];
    let currentIndex = 0;
    
    const interval = setInterval(() => {
      setIsAnimating(true);
      setTimeout(() => {
        currentIndex = (currentIndex + 1) % texts.length;
        setPlaceholderText(texts[currentIndex]);
        setIsAnimating(false);
      }, 500); // Half of the 1 second for animation
    }, 2000); // Change text every 2 seconds (1s for animation + 1s static)

    return () => clearInterval(interval);
  }, []);

  const frameworks = createListCollection({
    items: [
      { label: "All", value: "all" },
      { label: "Users", value: "7" },
      { label: "IB", value: "6" },
    ],
  });

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setDebouncedQuery(searchQuery.trim());
      setCurrentPage(1);
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchQuery]);

  const handleInputChange = (e) => {
    let value = e?.target?.value;

    if (value?.length === 1 && SPECIAL_CHARS.test(value)) {
      return;
    }

    setSearchQuery(value);
  };

  useEffect(() => {
    setisDataLoading(true);
    let payload
    if (whitelabelLocalStorage !== null) {
      let whiteLabelID = whitelabelLocalStorage._id
      payload = {
        currentPage,
        rowCount,
        whiteLabelID,
        roleFilter: "users",
        debouncedQuery,
        roleType: type
      };
    }
    else {
      payload = {
        currentPage,
        rowCount,
        roleFilter: "users",
        debouncedQuery,
        roleType: type
      }
    }
    dispatch(
      getUsersList({
        payload,
        callback: (data) => {
          if (data.meta.code === 200) {
            setAllUsersList(data?.data?.users);
            setisDataLoading(false);
            setTotalPagesCount(data?.meta?.totalPages);
          }
        },
      })
    );
  }, [whiteLabelDetail, currentPage, refetch, debouncedQuery, type]);

  const handleCopy = (item, type) => {
    let textToCopy;
    if (type === "email") {
      textToCopy = item.email;
    } else if (type === "username") {
      textToCopy = item?.externalUserData?.username;
    }
    navigator.clipboard.writeText(textToCopy || "").then(() => {
      setCopiedId(item._id);
      setCopiedType(type);
      setTimeout(() => {
        setCopiedId(null);
        setCopiedType(null);
      }, 1500);
    });
  };

  const handleUserStatus = (arg) => {
    dispatch(updateUserStatus({ status: arg?.status ? 1 : 0, id: arg?.id }));
  };

  // Delete asks for confirmation first: the icon only selects the user,
  // the dialog's "Delete user" button performs the deletion.
  const [userPendingDelete, setUserPendingDelete] = useState(null);
  const [isDeletingUser, setIsDeletingUser] = useState(false);

  const closeDeleteDialog = () => {
    if (isDeletingUser) return;
    setUserPendingDelete(null);
  };

  const handleDeleteUser = (id) => {
    setIsDeletingUser(true);
    dispatch(
      deleteUser({
        id,
        callback: (data) => {
          setIsDeletingUser(false);
          if (data?.meta?.code === 200) {
            setUserPendingDelete(null);
            handlerefetchData();
          }
        },
      })
    );
  };

  const handlerefetchData = () => setRefetch(!refetch);

  const handleViewKycDetails = (user) => {
    // Users created from "Add New User" carry every typed document
    if (Array.isArray(user?.kyc_documents) && user.kyc_documents.length) {
      const documents = user.kyc_documents
        .filter((doc) => doc?.url)
        .map((doc) => {
          const fileUrl = doc.url.startsWith('http') ? doc.url : `${S3_BUCKET_BASE_URL}/${doc.url}`;
          const typeLabel = KYC_DOCUMENT_LABELS[doc.docType] || "Document";
          const sideLabel = doc.side === "back" ? " (Back)" : "";
          return { pdf: fileUrl.toLowerCase().endsWith(".pdf"), fileUrl, name: `${typeLabel}${sideLabel}` };
        });
      if (documents.length) {
        setKycData(documents);
        setDialogOpen(true);
        return;
      }
    }

    const kycImages = user?.kyc_images;
    if (!kycImages) return;
    
    // Process all KYC keys including agreement, skip empty strings
    const images = ["front", "back", "other", "agreement"]
      .filter((key) => kycImages[key] && kycImages[key].trim() !== "")
      .map((key) => {
        const file = kycImages[key];
        // Check if file is already a full URL (starts with http/https)
        const fileUrl = file.startsWith('http') ? file : `${S3_BUCKET_BASE_URL}/${file}`;
        const isPdf = file?.toLowerCase().endsWith(".pdf");
        return isPdf
          ? { pdf: true, fileUrl, name: KYC_KEYS[key] }
          : { pdf: false, fileUrl, name: KYC_KEYS[key] };
      });
    
    if (images.length === 0) return;
    
    setKycData(images);
    setDialogOpen(true);
  };

  const handleEditPassword = (data) => {
    setEditPassword(true);
    setEditPasswordData(data);
  };

  const handleEditPromoCode = (data) => {
    setEditPromoCode(true);
    setEditPromoCodeData(data);
  };

  // New function to handle deposit action
  const handleDepositClick = (user) => {
    setSelectedUserForDeposit(user);
    setIsDepositModalOpen(true);
    setAdminPassword(""); // Reset password when opening modal
    setShowPassword(false); // Reset password visibility when opening modal
  };

  // New function to handle deposit submission
  const handleDepositSubmit = () => {
    if (!depositAmount || isNaN(depositAmount) || parseFloat(depositAmount) <= 0) {
      return;
    }

    const payload = {
      payload: {
        amount: parseFloat(depositAmount),
        userId: selectedUserForDeposit._id,
        email: selectedUserForDeposit.email,
        role: selectedUserForDeposit.role,
        accountno: selectedUserForDeposit.accountno,
        paramtype: ADD_BALANCE_PARAMS,
        password: adminPassword,
        whitelabelId: whitelabelLocalStorage?._id
      },
      callback: (data) => {
        if (data.meta.code === 200) {
          setIsDepositModalOpen(false);
          setDepositAmount("");
          setAdminPassword("");
          setSelectedUserForDeposit(null);
          handlerefetchData();
        } else {
        }
      },
    };

    dispatch(addUserBalance(payload));
  };

  function formatDate(isoDate) {
    if (!isoDate) return '--';

    const date = new Date(isoDate);

    const formatted = date.toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });

    return formatted?.replace(/(am|pm)/i, match => match.toUpperCase());
  }

  const downloadAllUsersCSV = () => {
    setIsDownloading(true);
    let payload;
    if (whitelabelLocalStorage !== null) {
      let whiteLabelID = whitelabelLocalStorage._id;
      payload = {
        currentPage: 1,
        rowCount: 10000,
        whiteLabelID,
        roleFilter: "users",
        debouncedQuery,
        roleType: type
      };
    } else {
      payload = {
        currentPage: 1,
        rowCount: 10000,
        roleFilter: "users",
        debouncedQuery,
        roleType: type
      };
    }

    const callback = (data) => {
      setIsDownloading(false);
      if (data.meta.code === 200) {
        const users = extractUsersFromData(data?.data) || [];
        
        if (users.length === 0) {
          alert("No users found to download");
          return;
        }

        try {
          const headers = [
            "Full Name",
            "Email",
            "Balance",
            "Account No.",
            "Mobile",
            "Country Code",
            "Promo Code",
            "Registration Date",
            "Status"
          ];

          const csvContent = [
            headers.join(","),
            ...users.map(user => [
              `"${user.fname} ${user.lname}"`,
              `"${user.email}"`,
              // 9xTrader users export their trading balance (mt5balance); ARC users unchanged
              isNineXTradingUser(user) ? Number(user.mt5balance) || 0 : user.balance,
              `"${user.accountno}"`,
              `"${user.mobile}"`,
              `"${user.countryCode || ''}"`,
              `"${user.promoCode || ''}"`,
              `"${user.createdAt || ''}"`,
              user.status ? "Active" : "Inactive"
            ].join(","))
          ].join("\n");

          const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.setAttribute("href", url);
          link.setAttribute("download", `users_list_${new Date().toISOString().slice(0, 10)}.csv`);
          link.style.visibility = "hidden";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        } catch (error) {
          console.error("Error generating CSV:", error);
          alert("Error generating CSV file: " + error.message);
        }
      } else {
        alert("Failed to download users data: " + (data.meta?.message || "Unknown error"));
      }
    };

    dispatch(getUsersList({ payload, callback }));
  };

  const downloadAllUsersPDF = () => {
    setIsDownloading(true);
    let payload;
    if (whitelabelLocalStorage !== null) {
      let whiteLabelID = whitelabelLocalStorage._id;
      payload = {
        currentPage: 1,
        rowCount: 10000,
        whiteLabelID,
        roleFilter: "users",
        debouncedQuery,
        roleType: type
      };
    } else {
      payload = {
        currentPage: 1,
        rowCount: 10000,
        roleFilter: "users",
        debouncedQuery,
        roleType: type
      };
    }

    const callback = (data) => {
      setIsDownloading(false);

      if (data.meta.code === 200) {
        const users = extractUsersFromData(data?.data) || [];
        
        if (users.length === 0) {
          alert("No users found to download");
          return;
        }

        try {
          const doc = new jsPDF('p', 'mm', 'a4');
          
          doc.setProperties({
            title: "Users List Report",
            subject: "User Data Export",
            author: "MoneyWallet Admin",
            keywords: "users, report, export, moneywallet",
            creator: "MoneyWallet Admin System"
          });

          doc.setFillColor(22, 160, 133);
          doc.rect(0, 0, doc.internal.pageSize.width, 35, 'F');
          
          doc.setFontSize(24);
          doc.setTextColor(255, 255, 255);
          doc.setFont(undefined, 'bold');
          doc.text("Users List Report", doc.internal.pageSize.width / 2, 18, { align: 'center' });

          doc.setFontSize(12);
          doc.setFont(undefined, 'normal');
          const dateStr = `Generated on: ${new Date().toLocaleDateString()}`;
          const countStr = `Total Users: ${users.length}`;
          
          doc.text(dateStr, 14, 28);
          doc.text(countStr, doc.internal.pageSize.width - 14, 28, { align: 'right' });

          doc.setDrawColor(255, 255, 255);
          doc.setLineWidth(0.5);
          doc.line(14, 32, doc.internal.pageSize.width - 14, 32);
          
          doc.setFontSize(10);
          doc.setFont(undefined, 'bold');
          doc.setTextColor(255, 255, 255);
          const headerY = 40;
          doc.text("S.No.", 15, headerY);
          doc.text("Full Name", 28, headerY);
          doc.text("Email", 55, headerY);
          doc.text("Balance", 92, headerY);
          doc.text("Account No.", 115, headerY);
          doc.text("Mobile", 140, headerY);
          doc.text("Promo Code", 165, headerY);
          doc.text("Reg. Date", 185, headerY);
          doc.text("Status", 207, headerY);

          const tableData = users.map((user, index) => [
            (index + 1).toString(),
            `${user.fname} ${user.lname}`,
            user.email,
            // 9xTrader users export their trading balance (mt5balance); ARC users unchanged
            (isNineXTradingUser(user) ? Number(user.mt5balance) || 0 : user.balance).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
            user.accountno,
            `${user.countryCode || ''} ${user.mobile}`,
            user.promoCode || '-',
            user.createdAt ? new Date(user.createdAt).toLocaleDateString() : '-',
            user.status ? "Active" : "Inactive"
          ]);

          autoTable(doc, {
            startY: 45,
            head: [[
              "S.No.",
              "Full Name",
              "Email",
              "Balance",
              "Account No.",
              "Mobile",
              "Promo Code",
              "Reg. Date",
              "Status"
            ]],
            body: tableData,
            theme: 'grid',
            styles: {
              fontSize: 8,
              cellPadding: 2.5,
              overflow: 'linebreak',
              cellWidth: 'wrap'
            },
            headStyles: {
              fillColor: [22, 160, 133],
              textColor: 255,
              fontStyle: 'bold',
              lineWidth: 0.1,
              lineColor: [22, 160, 133]
            },
            bodyStyles: {
              textColor: [40, 40, 40],
              lineWidth: 0.1,
              lineColor: [200, 200, 200]
            },
            alternateRowStyles: {
              fillColor: [245, 245, 245]
            },
            columnStyles: {
              0: { cellWidth: 12 },
              1: { cellWidth: 25 },
              2: { cellWidth: 35 },
              3: { cellWidth: 20, halign: 'right' },
              4: { cellWidth: 22 },
              5: { cellWidth: 22 },
              6: { cellWidth: 18 },
              7: { cellWidth: 22 },
              8: { cellWidth: 20 }
            },
            margin: { top: 45, left: 10, right: 10, bottom: 20 },
            pageBreak: 'auto',
            rowPageBreak: 'avoid',
            tableWidth: 'auto',
            showHead: 'everyPage',
            didDrawPage: function (data) {
              doc.setFontSize(10);
              doc.setTextColor(150);
              const pageCount = doc.getNumberOfPages();
              const pageText = `Page ${data.pageNumber} of ${pageCount}`;
              doc.text(pageText, data.settings.margin.left, doc.internal.pageSize.height - 10);
            }
          });

          const filename = `users_list_${new Date().toISOString().slice(0, 10)}_${new Date().getTime()}.pdf`;
          doc.save(filename);
        } catch (error) {
          console.error("Error generating PDF:", error);
          alert("Error generating PDF file: " + error.message);
        }
      } else {
        alert("Failed to download users data: " + (data.meta?.message || "Unknown error"));
      }
    };

    dispatch(getUsersList({ payload, callback }));
  };



  // New function to handle trades action
  const handleViewTrades = (user) => {
    setSelectedUserForTrades(user);
    setIsTradesModalOpen(true);
  };

  const handleKycDocumentUpload = (user) => {
    setSelectedUserForKycUpload(user);
    setIsKycUploadModalOpen(true);
  };

  const handleKycSubmit = (data) => {
    console.log('handleKycSubmit called with data:', data); // Debug log
    dispatch(
      adminSubmitUserKyc({
        userId: data.userId._id, // Extract the actual user ID
        files: data.files,
        callback: (response) => {
          console.log('Callback received response:', response); // Debug log
          if (response?.meta?.code === 200) {
            setIsKycUploadModalOpen(false);
            setSelectedUserForKycUpload(null);
            handlerefetchData();
          }
        },
      })
    );
  };

  return (
    <>
      {
        selectedIB ? (
          <IBDetailPage
            ibData={selectedIB}
            onBack={() => setSelectedIB(null)}
          />
        ) : <VStack gap={5} align="stretch" width="100%">
          {/* Page header */}
          <Flex
            direction={{ base: "column", md: "row" }}
            justify="space-between"
            align={{ base: "stretch", md: "center" }}
            gap={3}
          >
            <Text fontSize="sm" color="fg.muted">
              Manage client accounts, KYC, credentials and wallet balances.
            </Text>
            <HStack gap={2}>
              <Button
                size="sm"
                variant="outline"
                bg="bg.surface"
                onClick={downloadAllUsersCSV}
                loading={isDownloading}
                loadingText="Exporting"
              >
                <FiDownload /> Export CSV
              </Button>
              <Button
                size="sm"
                variant="outline"
                bg="bg.surface"
                onClick={downloadAllUsersPDF}
                loading={isDownloading}
                loadingText="Exporting"
              >
                <FiDownload /> Export PDF
              </Button>
            </HStack>
          </Flex>

          <Box
            bg="bg.surface"
            border="1px solid"
            borderColor="border.subtle"
            borderRadius="xl"
            boxShadow="card"
            overflow="hidden"
          >
            {/* Toolbar */}
            <Flex
              direction={{ base: "column", md: "row" }}
              gap={3}
              px={4}
              py={3}
              align={{ base: "stretch", md: "center" }}
              borderBottom="1px solid"
              borderColor="border.subtle"
            >
              <Select.Root
                collection={frameworks}
                width={{ base: "100%", md: "180px" }}
                size="sm"
                disabled={isDataLoading}
                onChange={(e) => {
                  setType(e?.target?.value);
                  setCurrentPage(1);
                }}
                deselectable
                defaultValue={["all"]}
              >
                <Select.HiddenSelect />

                <Select.Control>
                  <Select.Trigger borderRadius="md">
                    <Select.ValueText placeholder="Select Role" />
                  </Select.Trigger>
                  <Select.IndicatorGroup>
                    <Select.ClearTrigger />
                    <Select.Indicator />
                  </Select.IndicatorGroup>
                </Select.Control>
                <Portal>
                  <Select.Positioner>
                    <Select.Content>
                      {frameworks.items.map((framework) => (
                        <Select.Item item={framework} key={framework.value}>
                          {framework.label}
                          <Select.ItemIndicator />
                        </Select.Item>
                      ))}
                    </Select.Content>
                  </Select.Positioner>
                </Portal>
              </Select.Root>

              <Box position="relative" width={{ base: "100%", md: "340px" }}>
                <Box
                  position="absolute"
                  left="10px"
                  top="50%"
                  transform="translateY(-50%)"
                  color="fg.subtle"
                  pointerEvents="none"
                  display="flex"
                  zIndex={1}
                >
                  <FiSearch size={15} />
                </Box>
                <Input
                  size="sm"
                  borderRadius="md"
                  ps="32px"
                  placeholder=""
                  value={searchQuery}
                  onChange={handleInputChange}
                  width="100%"
                />
                <Box
                  position="absolute"
                  left="32px"
                  top="50%"
                  transform="translateY(-50%)"
                  color="fg.subtle"
                  fontSize="sm"
                  pointerEvents="none"
                  display={searchQuery ? "none" : "flex"}
                  alignItems="center"
                >
                  <span>Search by&nbsp;</span>
                  <span
                    style={{
                      display: 'inline-block',
                      transition: 'transform 0.5s ease-in-out, opacity 0.5s ease-in-out',
                      transform: isAnimating ? 'translateY(-10px)' : 'translateY(0)',
                      opacity: isAnimating ? 0 : 1
                    }}
                  >
                    {placeholderText.toLowerCase()}
                  </span>
                </Box>
              </Box>

              <HStack gap={4} ms={{ md: "auto" }} fontSize="xs" color="fg.muted">
                <HStack gap={1.5}>
                  <Box w="8px" h="8px" borderRadius="full" bg="brand.500" />
                  <Text>IB</Text>
                </HStack>
                <HStack gap={1.5}>
                  <Box w="8px" h="8px" borderRadius="full" bg="gray.500" />
                  <Text>User</Text>
                </HStack>
              </HStack>
            </Flex>

          {isDataLoading ? (
            <Box p={4}>
              <UserListSkeleton />
            </Box>
          ) : (
            <Box
              overflow="auto"
              maxHeight="calc(100vh - 290px)"
              width="100%"
            >
              <Table.Root size="sm" stickyHeader width="100%" css={{ "& td": { borderColor: "border.subtle" } }}>
                <Table.Header>
                  <Table.Row bg="gray.50">
                    {[
                      "User",
                      "Username",
                      "Wallet Balance",
                      "Account No.",
                      "Mobile",
                      "Promo Code",
                      "KYC",
                      "Password",
                      "Registered",
                      "Actions",
                    ].map((heading) => (
                      <Table.ColumnHeader
                        key={heading}
                        py={3}
                        px={4}
                        bg="gray.50"
                        color="fg.muted"
                        fontSize="11px"
                        fontWeight="600"
                        textTransform="uppercase"
                        letterSpacing="0.05em"
                        whiteSpace="nowrap"
                        borderColor="border.subtle"
                        textAlign={heading === "Wallet Balance" ? "right" : "left"}
                      >
                        {heading === "Actions" ? (
                          <Box>
                            <Text>{heading}</Text>
                            <HStack gap={1} mt={1}>
                              {ACTION_COLUMNS.map((label) => (
                                <Text
                                  key={label}
                                  w={ACTION_SLOT_WIDTH}
                                  textAlign="center"
                                  fontSize="10px"
                                  fontWeight="500"
                                  color="fg.subtle"
                                  letterSpacing="0.02em"
                                >
                                  {label}
                                </Text>
                              ))}
                            </HStack>
                          </Box>
                        ) : (
                          heading
                        )}
                      </Table.ColumnHeader>
                    ))}
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {allUsersList?.length > 0 ? (
                    allUsersList.map((item) => {
                      const isIB = item.role === 6;
                      const kyc = KYC_STATUS_STYLES[item.kyc_status];
                      return (
                      <Table.Row
                        key={item._id}
                        _hover={{ bg: "gray.50" }}
                        transition="background 120ms ease"
                      >
                        <Table.Cell px={4} py={3} minW="240px">
                          <HStack gap={3} align="center">
                            <Flex
                              flexShrink={0}
                              w="36px"
                              h="36px"
                              borderRadius="full"
                              align="center"
                              justify="center"
                              fontSize="xs"
                              fontWeight="700"
                              bg={isIB ? "brand.50" : "gray.100"}
                              color={isIB ? "brand.700" : "gray.600"}
                            >
                              {`${item.fname?.[0] || ""}${item.lname?.[0] || ""}`.toUpperCase() || "U"}
                            </Flex>
                            <Box minW={0}>
                              <HStack gap={1.5}>
                                <Text
                                  fontSize="sm"
                                  fontWeight="600"
                                  color={isIB ? "brand.700" : "fg.default"}
                                  cursor={isIB ? "pointer" : "default"}
                                  _hover={isIB ? { textDecoration: "underline" } : undefined}
                                  onClick={() => {
                                    if (item.role === 6) setSelectedIB(item);
                                  }}
                                  lineClamp={1}
                                  textTransform="capitalize"
                                >
                                  {item.fname} {item.lname}
                                </Text>
                                {isIB && (
                                  <Badge size="xs" colorPalette="blue" variant="subtle" borderRadius="sm">
                                    IB
                                  </Badge>
                                )}
                                {item.currency && (
                                  <Badge size="xs" variant="outline" color="fg.muted" borderRadius="sm">
                                    {item.currency}
                                  </Badge>
                                )}
                              </HStack>
                              <HStack
                                as="button"
                                type="button"
                                gap={1}
                                mt={0.5}
                                fontSize="xs"
                                color="fg.muted"
                                cursor="pointer"
                                _hover={{ color: "fg.default" }}
                                onClick={() => handleCopy(item, "email")}
                                title="Copy email"
                              >
                                <Text lineClamp={1}>{item.user_name || item.email}</Text>
                                {copiedId === item._id && copiedType === "email" ? (
                                  <Box color="green.600" display="flex"><IoCheckmarkDoneOutline /></Box>
                                ) : (
                                  <Box display="flex"><FaRegCopy size={11} /></Box>
                                )}
                              </HStack>
                            </Box>
                          </HStack>
                        </Table.Cell>
                        <Table.Cell px={4}>
                          {item?.externalUserData?.username ? (
                            <HStack
                              as="button"
                              type="button"
                              gap={1.5}
                              px={2}
                              py={1}
                              borderRadius="md"
                              bg="gray.100"
                              fontSize="xs"
                              fontFamily="mono"
                              color="fg.default"
                              cursor="pointer"
                              _hover={{ bg: "gray.200" }}
                              onClick={() => handleCopy(item, "username")}
                              title="Copy username"
                            >
                              <Text>{item?.externalUserData?.username}</Text>
                              {copiedId === item._id && copiedType === "username" ? (
                                <Box color="green.600" display="flex"><IoCheckmarkDoneOutline /></Box>
                              ) : (
                                <Box display="flex" color="fg.subtle"><FaRegCopy size={11} /></Box>
                              )}
                            </HStack>
                          ) : (
                            <Text color="fg.subtle">—</Text>
                          )}
                        </Table.Cell>
                        <Table.Cell px={4} textAlign="right" fontWeight="600" color="fg.default" whiteSpace="nowrap">
                          {/* 9xTrader users show their trading balance (mt5balance); ARC users unchanged */}
                          {(Number(isNineXTradingUser(item) ? item?.mt5balance : item?.balance) || 0).toFixed(2)}
                        </Table.Cell>
                        <Table.Cell px={4} fontFamily="mono" fontSize="xs" color="fg.default">
                          {item?.accountno || <Text as="span" color="fg.subtle">—</Text>}
                        </Table.Cell>
                        <Table.Cell px={4} color="fg.muted" whiteSpace="nowrap">
                          {item?.countryCode} {item?.mobile}
                        </Table.Cell>
                        <Table.Cell px={4}>
                          <HStack gap={2} justifyContent="space-between">
                            <Text color={item?.promoCode ? "fg.default" : "fg.subtle"}>{item?.promoCode || "—"}</Text>
                            <ActionIcon label="Edit promo code" onClick={() => handleEditPromoCode(item)}>
                              <FiEdit />
                            </ActionIcon>
                          </HStack>
                        </Table.Cell>
                        <Table.Cell px={4}>
                          {item.role === 2 ? (
                            "-"
                          ) : item.role === 6 || item.role === 7 ? (
                            <HStack gap={1.5}>
                              <HStack
                                as="span"
                                gap={1.5}
                                px={2}
                                py={0.5}
                                borderRadius="full"
                                fontSize="xs"
                                fontWeight="500"
                                whiteSpace="nowrap"
                                bg={kyc?.bg || "gray.100"}
                                color={kyc?.color || "gray.700"}
                              >
                                <Box w="6px" h="6px" borderRadius="full" bg={kyc?.dot || "gray.400"} />
                                {kyc?.label || "-"}
                              </HStack>
                              {item.kyc_status === 3 ? ( // Not Submitted
                                <ActionIcon label="Upload KYC documents" onClick={() => handleKycDocumentUpload(item)}>
                                  <FiUpload />
                                </ActionIcon>
                              ) : (
                                <ActionIcon label="View KYC documents" onClick={() => handleViewKycDetails(item)}>
                                  <FaRegEye />
                                </ActionIcon>
                              )}
                            </HStack>
                          ) : null}
                        </Table.Cell>

                        <Table.Cell px={4}>
                          <HStack gap={0.5}>
                            <ActionIcon label="View password" onClick={() => setViewPasswordUser(item)}>
                              <FaRegEye />
                            </ActionIcon>
                            <ActionIcon label="Change password" onClick={() => handleEditPassword(item)}>
                              <FiEdit />
                            </ActionIcon>
                          </HStack>
                        </Table.Cell>
                        <Table.Cell px={4} color="fg.muted" fontSize="xs" whiteSpace="nowrap">
                          {formatDate(item?.createdAt)}
                        </Table.Cell>
                        <Table.Cell px={4}>
                          <HStack gap={1}>
                            <Flex w={ACTION_SLOT_WIDTH} justify="center">
                              <Tooltip
                                content={
                                  <ActionTooltipContent
                                    label={item?.status ? "Account active" : "Account inactive"}
                                    description={
                                      item?.status
                                        ? "Click to deactivate this account."
                                        : "Click to activate this account."
                                    }
                                  />
                                }
                                showArrow
                              >
                                <Box>
                                  <ToggleButton
                                    onToggle={handleUserStatus}
                                    toggle={item?.status ? true : false}
                                    identifier={item?._id}
                                  />
                                </Box>
                              </Tooltip>
                            </Flex>
                            <Flex w={ACTION_SLOT_WIDTH} justify="center">
                              <ActionIcon
                                label="View trades"
                                description="Opens this user's trade history. Nothing is changed."
                                onClick={() => handleViewTrades(item)}
                              >
                                <FiTrendingUp />
                              </ActionIcon>
                            </Flex>
                            <Flex w={ACTION_SLOT_WIDTH} justify="center">
                              <ActionIcon
                                label="Add funds"
                                description={`${
                                  isIB
                                    ? "Adds funds to this IB's wallet balance."
                                    : isNineXTradingUser(item)
                                      ? "Credits funds to this user's 9xTrader trading account."
                                      : "Credits funds to this user's trading account."
                                } Opens a form; you confirm with your admin password.`}
                                palette="green"
                                onClick={() => handleDepositClick(item)}
                              >
                                <FiPlusCircle />
                              </ActionIcon>
                            </Flex>
                            <Flex w={ACTION_SLOT_WIDTH} justify="center">
                              <ActionIcon
                                label="Delete user"
                                description="Asks you to confirm, then marks the account deleted and removes it from this list."
                                palette="red"
                                onClick={() => setUserPendingDelete(item)}
                              >
                                <FiTrash2 />
                              </ActionIcon>
                            </Flex>
                          </HStack>
                        </Table.Cell>
                      </Table.Row>
                      );
                    })
                  ) : (
                    <Table.Row>
                      <Table.Cell colSpan={10} textAlign="center" py={16}>
                        <Text fontSize="sm" fontWeight="500" color="fg.default">
                          No users found
                        </Text>
                        <Text fontSize="xs" color="fg.subtle" mt={1}>
                          Try a different search or role filter.
                        </Text>
                      </Table.Cell>
                    </Table.Row>
                  )}
                </Table.Body>

              </Table.Root>
            </Box>
          )}

            <Flex
              px={4}
              py={3}
              justifyContent="center"
              borderTop="1px solid"
              borderColor="border.subtle"
            >
              <CustomPagination
                totalCount={totalPagesCount}
                current={currentPage}
                setCurrent={setCurrentPage}
                isLoading={isDataLoading}
              />
            </Flex>
          </Box>
        </VStack>
      }

      <Dialog.Root
        size="sm"
        placement="center"
        motionPreset="scale"
        role="alertdialog"
        open={Boolean(userPendingDelete)}
        onOpenChange={(e) => {
          if (!e.open) closeDeleteDialog();
        }}
      >
        <Portal>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header>
                <Dialog.Title>Delete this user?</Dialog.Title>
              </Dialog.Header>
              <Dialog.Body>
                <Box p={3} mb={3} borderRadius="md" bg="gray.50" border="1px solid" borderColor="border.subtle">
                  <Text fontSize="sm" fontWeight="600" textTransform="capitalize">
                    {userPendingDelete?.fname} {userPendingDelete?.lname}
                  </Text>
                  <Text fontSize="xs" color="fg.muted">
                    {userPendingDelete?.email}
                    {userPendingDelete?.accountno ? ` · Account ${userPendingDelete.accountno}` : ""}
                  </Text>
                </Box>
                <Text fontSize="sm" color="fg.muted">
                  The account will be marked deleted and removed from this list.
                </Text>
              </Dialog.Body>
              <Dialog.Footer>
                <Button variant="outline" size="sm" onClick={closeDeleteDialog} disabled={isDeletingUser}>
                  Cancel
                </Button>
                <Button
                  colorPalette="red"
                  size="sm"
                  loading={isDeletingUser}
                  loadingText="Deleting…"
                  onClick={() => handleDeleteUser(userPendingDelete?._id)}
                >
                  Delete user
                </Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>

      {dialogOpen && (
        <MultiImageDialog
          images={kycData}
          isOpen={dialogOpen}
          onClose={() => setDialogOpen(false)}
        />
      )}

      {editPassword && editPasswordData && (
        <PasswordChangeModal
          data={editPasswordData}
          isOpen={editPassword}
          onClose={() => {
            setEditPassword(false);
            setEditPasswordData(null);
          }}
          refetchData={handlerefetchData}
        />
      )}

      {editPromoCode && editPromoCodeData && (
        <PromoCodeEditModal
          data={editPromoCodeData}
          isOpen={editPromoCode}
          onClose={() => {
            setEditPromoCode(false);
            setEditPromoCodeData(null);
          }}
          refetchData={handlerefetchData}
        />
      )}

      {viewPasswordUser && (
        <ViewPasswordModal
          user={viewPasswordUser}
          isOpen={!!viewPasswordUser}
          onClose={() => setViewPasswordUser(null)}
        />
      )}

      {/* Custom Deposit Modal */}
      {isDepositModalOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(16, 24, 40, 0.45)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
          }}
          onClick={() => setIsDepositModalOpen(false)}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              boxShadow: '0 24px 48px -12px rgba(16, 24, 40, 0.25)',
              width: '90%',
              maxWidth: '500px',
              maxHeight: '90vh',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '20px 24px',
                borderBottom: '1px solid #e2e8f0',
                background: '#ffffff',
              }}
            >
              <h2 style={{ fontSize: '18px', fontWeight: '600', margin: 0, color: '#101828', letterSpacing: '-0.01em' }}>
                Add User Balance
              </h2>
              <button
                onClick={() => setIsDepositModalOpen(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'background-color 0.2s',
                }}
                onMouseOver={(e) => e.target.style.backgroundColor = '#edf2f7'}
                onMouseOut={(e) => e.target.style.backgroundColor = 'transparent'}
              >
                <FiX size={20} color="#4a5568" />
              </button>
            </div>

            {/* Modal Body */}
            <div style={{ padding: '24px', overflowY: 'auto', flex: 1 }}>
              {selectedUserForDeposit && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  {/* User Information Card */}
                  <div
                    style={{
                      backgroundColor: '#f8fafc',
                      padding: '20px',
                      borderRadius: '10px',
                      border: '1px solid #e2e8f0',
                      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
                    }}
                  >
                    <h3 style={{ 
                      fontSize: '16px', 
                      fontWeight: '600', 
                      marginBottom: '16px', 
                      color: '#2d3748',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <span style={{ 
                        display: 'inline-block',
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: '#3182ce'
                      }}></span>
                      User Information
                    </h3>
                    
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                      <div>
                        <p style={{ margin: '0 0 4px 0', fontSize: '12px', color: '#718096' }}>
                          Full Name
                        </p>
                        <p style={{ margin: '0', fontSize: '14px', fontWeight: '500', color: '#2d3748' }}>
                          {selectedUserForDeposit.fname} {selectedUserForDeposit.lname}
                        </p>
                      </div>
                      
                      <div>
                        <p style={{ margin: '0 0 4px 0', fontSize: '12px', color: '#718096' }}>
                          Role
                        </p>
                        <p style={{ margin: '0', fontSize: '14px', fontWeight: '500', color: '#2d3748' }}>
                          {selectedUserForDeposit.role === 6 ? 'IB' : selectedUserForDeposit.role === 7 ? 'User' : 'Other'}
                        </p>
                      </div>
                      
                      <div>
                        <p style={{ margin: '0 0 4px 0', fontSize: '12px', color: '#718096' }}>
                          Email
                        </p>
                        <p style={{ margin: '0', fontSize: '14px', fontWeight: '500', color: '#2d3748' }}>
                          {selectedUserForDeposit.email}
                        </p>
                      </div>
                      
                      <div>
                        <p style={{ margin: '0 0 4px 0', fontSize: '12px', color: '#718096' }}>
                          Account No
                        </p>
                        <p style={{ margin: '0', fontSize: '14px', fontWeight: '500', color: '#2d3748' }}>
                          {selectedUserForDeposit.role === 7 ? selectedUserForDeposit.accountno : 'N/A'}
                        </p>
                      </div>
                      
                      <div style={{ gridColumn: 'span 2' }}>
                        <p style={{ margin: '0 0 4px 0', fontSize: '12px', color: '#718096' }}>
                          Current Balance
                        </p>
                        <p style={{ margin: '0', fontSize: '16px', fontWeight: '600', color: '#3182ce' }}>
                          {selectedUserForDeposit.balance} {selectedUserForDeposit.currency}
                        </p>
                      </div>
                    </div>
                    {/* 9xTrader users only — renders nothing for ARC users */}
                    <NineXTradingAccountCard user={selectedUserForDeposit} />
                  </div>

                  {/* Amount Input */}
                  <div>
                    <label
                      style={{
                        display: 'block',
                        marginBottom: '8px',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#2d3748',
                      }}
                    >
                      Amount to Add
                    </label>
                    <input
                      type="number"
                      value={depositAmount}
                      onChange={(e) => setDepositAmount(e.target.value)}
                      placeholder="Enter amount"
                      min="0"
                      step="0.01"
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        borderRadius: '8px',
                        border: '1px solid #e2e8f0',
                        fontSize: '14px',
                        boxSizing: 'border-box',
                        transition: 'border-color 0.2s, box-shadow 0.2s',
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#3182ce';
                        e.target.style.boxShadow = '0 0 0 3px rgba(49, 130, 206, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#e2e8f0';
                        e.target.style.boxShadow = 'none';
                      }}
                    />
                  </div>
                  
                  {/* Admin Password */}
                  <div>
                    <label
                      style={{
                        display: 'block',
                        marginBottom: '8px',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#2d3748',
                      }}
                    >
                      Admin Password
                    </label>
                    <div style={{ position: 'relative' }}>
                      <input
                        type={showPassword ? "text" : "password"}
                        value={adminPassword}
                        onChange={(e) => setAdminPassword(e.target.value)}
                        placeholder="Enter admin password"
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          borderRadius: '8px',
                          border: '1px solid #e2e8f0',
                          fontSize: '14px',
                          boxSizing: 'border-box',
                          transition: 'border-color 0.2s, box-shadow 0.2s',
                          paddingRight: '40px',
                        }}
                        onFocus={(e) => {
                          e.target.style.borderColor = '#3182ce';
                          e.target.style.boxShadow = '0 0 0 3px rgba(49, 130, 206, 0.1)';
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = '#e2e8f0';
                          e.target.style.boxShadow = 'none';
                        }}
                      />
                      <button
                        onClick={() => setShowPassword(!showPassword)}
                        style={{
                          position: 'absolute',
                          right: '12px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '4px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {showPassword ? <FaRegEyeSlash size={18} color="#718096" /> : <FaRegEye size={18} color="#718096" />}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '12px',
                padding: '20px 24px',
                borderTop: '1px solid #e2e8f0',
                background: '#f8fafc',
              }}
            >
              <button
                onClick={() => setIsDepositModalOpen(false)}
                style={{
                  padding: '10px 16px',
                  borderRadius: '6px',
                  border: '1px solid #e2e8f0',
                  backgroundColor: 'white',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#4a5568',
                  transition: 'all 0.2s',
                }}
                onMouseOver={(e) => {
                  e.target.style.backgroundColor = '#f7fafc';
                  e.target.style.borderColor = '#cbd5e0';
                }}
                onMouseOut={(e) => {
                  e.target.style.backgroundColor = 'white';
                  e.target.style.borderColor = '#e2e8f0';
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDepositSubmit}
                disabled={!adminPassword}
                style={{
                  padding: '10px 16px',
                  borderRadius: '6px',
                  border: 'none',
                  backgroundColor: adminPassword ? '#2549ea' : '#98a2b3',
                  color: 'white',
                  cursor: adminPassword ? 'pointer' : 'not-allowed',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'background-color 0.2s',
                }}
                onMouseOver={(e) => {
                  if (adminPassword) {
                    e.target.style.backgroundColor = '#1d38d7';
                  }
                }}
                onMouseOut={(e) => {
                  if (adminPassword) {
                    e.target.style.backgroundColor = '#2549ea';
                  }
                }}
              >
                Add Balance
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Trades Modal */}
      <UserTradesModal
        isOpen={isTradesModalOpen}
        onClose={() => {
          setIsTradesModalOpen(false);
          setSelectedUserForTrades(null);
        }}
        user={selectedUserForTrades}
      />

      {/* KYC Document Upload Modal */}
      <KYCDocumentUploadModal
        isOpen={isKycUploadModalOpen}
        onClose={() => {
          setIsKycUploadModalOpen(false);
          setSelectedUserForKycUpload(null);
        }}
        onSubmit={handleKycSubmit}
        userId={selectedUserForKycUpload}
      />
    </>
  );
};